/** Unicode Indian Rupee sign (₹). */
export const INR = "\u20B9";

const LOCALE = "en-IN";

export function formatInr(
  amount: number,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number },
): string {
  const n = Number.isFinite(amount) ? amount : 0;
  const min = options?.minimumFractionDigits ?? 2;
  const max = options?.maximumFractionDigits ?? 2;
  return `${INR}${n.toLocaleString(LOCALE, { minimumFractionDigits: min, maximumFractionDigits: max })}`;
}

/** Whole rupees with Indian grouping (dashboard KPIs, admin tables). */
export function formatInrWhole(amount: number): string {
  const n = Number.isFinite(amount) ? amount : 0;
  return `${INR}${n.toLocaleString(LOCALE, { maximumFractionDigits: 0 })}`;
}

/** Like legacy POS grid: up to 2 decimals, trailing zeros trimmed, with grouping. */
export function formatInrTrimmed(amount: number): string {
  if (!Number.isFinite(amount)) return `${INR}0`;
  const trimmed = amount.toFixed(2).replace(/\.?0+$/, "");
  const [intPart, frac] = trimmed.split(".");
  const intStr = Number(intPart).toLocaleString(LOCALE);
  return frac !== undefined ? `${INR}${intStr}.${frac}` : `${INR}${intStr}`;
}

export function formatInrIntl(amount: number): string {
  const n = Number.isFinite(amount) ? amount : 0;
  return n.toLocaleString(LOCALE, { style: "currency", currency: "INR" });
}

export function formatInrThousandsTick(value: number): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return `${INR}0`;

  const abs = Math.abs(n);
  // Avoid confusing "₹0k" for small values; keep ticks readable and compact.
  if (abs < 1000) {
    return formatInrWhole(n);
  }

  // Indian-style large number suffixes for charts.
  if (abs < 100_000) {
    const k = n / 1000;
    const kStr = abs < 10_000 ? k.toFixed(1) : k.toFixed(0);
    return `${INR}${kStr.replace(/\.0$/, "")}k`;
  }
  if (abs < 10_000_000) {
    const l = n / 100_000;
    const lStr = abs < 1_000_000 ? l.toFixed(1) : l.toFixed(0);
    return `${INR}${lStr.replace(/\.0$/, "")}L`;
  }

  const cr = n / 10_000_000;
  const crStr = abs < 100_000_000 ? cr.toFixed(1) : cr.toFixed(0);
  return `${INR}${crStr.replace(/\.0$/, "")}Cr`;
}

/** Chart tooltips: grouped number, no forced fraction digits. */
export function formatInrLoose(value: number): string {
  const n = Number.isFinite(value) ? value : 0;
  return `${INR}${n.toLocaleString(LOCALE)}`;
}

/** Remove leading $ or ₹ when the UI already shows a rupee icon. */
export function stripCurrencySymbolPrefix(s: string): string {
  return s.replace(/^[\s$\u20B9\u00A0]+/, "").trim();
}
